import { LuIcon, Menu, type MenuRootProps, type MenuVariantProps } from "@overmap-ai/blocks"
import type { AssetTypeIdentifier, AssetTypeStatus, Stored } from "@overmap-ai/common"
import { type Field, FieldSchema } from "@overmap-ai/forms"
import { memo, type ReactElement, useCallback, useMemo } from "react"

import { AssetLabelFilterDef } from "../../filtering/defs/AssetLabelFilterDef"
import { AssetTypeStatusFilterDef } from "../../filtering/defs/AssetTypeStatusFilterDef"
import { IdentifierFilterDef } from "../../filtering/defs/IdentifierFilterDef"
import { SubmittedAtFilterDef } from "../../filtering/defs/SubmittedAtFilterDef"
import { getFilterDefOfField } from "../../filtering/defs/utils"
import { FilterManager } from "../../filtering/manager"
import { modelIconMapping } from "../../mappings/icons"
import type { AssetData } from "../../typings"
import { FieldGroup } from "../MenuGroups/FieldGroup"
import { IdentifierGroup } from "../MenuGroups/IdentifierGroup"

export interface AssetDataFilterMenuProps extends MenuRootProps, MenuVariantProps {
	children: ReactElement
	filterManager: FilterManager<AssetData>
	assetTypeIdentifiers: Stored<AssetTypeIdentifier>[]
	assetTypeFieldSchema: FieldSchema
	assetTypeStatuses: Stored<AssetTypeStatus>[]
}

export const AssetDataFilterMenu = memo<AssetDataFilterMenuProps>((props) => {
	const {
		children,
		variant,
		size,
		accentColor,
		filterManager,
		assetTypeIdentifiers,
		assetTypeFieldSchema,
		assetTypeStatuses,
		...rest
	} = props

	const handleSelectLabel = useCallback(() => {
		filterManager.addFilter(new AssetLabelFilterDef().createFilter())
	}, [filterManager])

	const handleSelectSubmittedAt = useCallback(() => {
		filterManager.addFilter(new SubmittedAtFilterDef().createFilter())
	}, [filterManager])

	const handleSelectStatus = useCallback(() => {
		if (!assetTypeStatuses) return

		filterManager.addFilter(new AssetTypeStatusFilterDef(assetTypeStatuses).createFilter())
	}, [assetTypeStatuses, filterManager])

	const handleIdentifierSelect = useCallback(
		(identifier: Stored<AssetTypeIdentifier>) => {
			filterManager.addFilter(new IdentifierFilterDef(identifier).createFilter())
		},
		[filterManager],
	)

	const handleFieldSelect = useCallback(
		(field: Field) => {
			filterManager.addFilter(getFilterDefOfField(field).createFilter())
		},
		[filterManager],
	)

	const supportedAssetTypeFields = useMemo(() => assetTypeFieldSchema?.getFields(), [assetTypeFieldSchema])

	return (
		<Menu.Root {...rest}>
			<Menu.ClickTrigger>{children}</Menu.ClickTrigger>
			<Menu.Content variant={variant} size={size} accentColor={accentColor}>
				<Menu.Scroll>
					<Menu.Item onSelect={handleSelectLabel}>
						<LuIcon icon={modelIconMapping.text} />
						Label
					</Menu.Item>

					<Menu.Item onSelect={handleSelectSubmittedAt}>
						<LuIcon icon={modelIconMapping.dueDate} />
						Submitted at
					</Menu.Item>

					{assetTypeStatuses.length > 0 && (
						<Menu.Item onSelect={handleSelectStatus}>
							<LuIcon icon={modelIconMapping.status} />
							Status
						</Menu.Item>
					)}

					{assetTypeIdentifiers.length > 0 && (
						<>
							<Menu.Label>Identifiers</Menu.Label>
							<IdentifierGroup
								identifiers={assetTypeIdentifiers}
								onIdentifierSelect={handleIdentifierSelect}
							/>
						</>
					)}

					{supportedAssetTypeFields.length > 0 && (
						<>
							<Menu.Label>Fields</Menu.Label>
							<FieldGroup fields={supportedAssetTypeFields} onFieldSelect={handleFieldSelect} />
						</>
					)}
				</Menu.Scroll>
			</Menu.Content>
		</Menu.Root>
	)
})
AssetDataFilterMenu.displayName = "AssetDataFilterMenu"
